import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowRight, TriangleAlert, Wind, Activity, ShieldAlert, House } from "lucide-react";

const FAQ_ITEMS = [
  { q: "What is radon?", a: "Radon is a naturally occurring radioactive gas that comes from the breakdown of uranium in soil, rock, and water. It's completely invisible, odorless, and tasteless, making it impossible to detect without proper testing equipment." },
  { q: "Why is radon dangerous?", a: "When radon is inhaled, radioactive particles become trapped in your lungs. Over time, these particles release energy that can damage lung tissue and lead to lung cancer. It's the #1 cause of lung cancer in non-smokers." },
  { q: "How does radon mitigation work?", a: "Radon mitigation systems use a fan and pipe system to draw radon gas from beneath the foundation of your home and vent it safely above the roofline. This can reduce radon levels by up to 99%." },
  { q: "How long does installation take?", a: "Most radon mitigation systems can be installed in a single day with minimal disruption to your home and daily routine." },
  { q: "Is the free radon test really free?", a: "Yes! Our free testing program is designed to remove financial barriers to radon awareness. There is absolutely no obligation to proceed with mitigation services after testing." },
];

export default function WhatIsRadonPage() {
  return (
    <main>
      <div className="min-h-screen">
        {/* Hero */}
        <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-red-50 via-white to-orange-50">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="animate-slide-in-left">
                <Badge className="mb-4 bg-red-100 text-red-700 hover:bg-red-100">Important Health Information</Badge>
                <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                  Understanding<span className="text-red-600"> Radon Risk</span>
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed">
                  Radon is a silent threat in many Canadian homes. Learn why testing and mitigation are essential for your family's health.
                </p>
              </div>
              <div className="animate-slide-in-right">
                <Card className="bg-white border-2 border-red-200">
                  <CardContent className="p-8">
                    <div className="flex items-start space-x-4 mb-6">
                      <TriangleAlert className="h-12 w-12 text-red-600 flex-shrink-0" />
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">Did You Know?</h3>
                        <p className="text-gray-700 leading-relaxed">
                          Radon is the <span className="font-semibold text-red-600">#1 cause of lung cancer</span> among non-smokers and is responsible for approximately <span className="font-semibold">3,300 deaths</span> in Canada each year.
                        </p>
                      </div>
                    </div>
                    <div className="bg-red-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-700"><span className="font-semibold">Health Canada recommends</span> taking action if radon levels exceed 200 Bq/m³.</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* What is Radon */}
        <section className="py-20 px-4 sm:px-6 lg:px-8" data-testid="what-is-radon-info">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">What is Radon?</h2>
                <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
                  <p>
                    Radon is a <span className="font-semibold">naturally occurring radioactive gas</span> that comes from the breakdown of uranium in soil, rock, and water. It's completely invisible, odorless, and tasteless, making it impossible to detect without proper testing equipment.
                  </p>
                  <p>This gas can enter your home through:</p>
                  <ul className="space-y-2 ml-6">
                    {["Cracks in foundations and floors", "Construction joints and gaps", "Openings around pipes and wiring", "Sump pump openings and floor drains"].map((item) => (
                      <li key={item} className="flex items-start space-x-2">
                        <span className="text-blue-600 font-bold">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Wind, title: "Invisible", desc: "Cannot be seen" },
                  { icon: Activity, title: "Odorless", desc: "No smell" },
                  { icon: ShieldAlert, title: "Radioactive", desc: "Health hazard" },
                  { icon: House, title: "Common", desc: "16% of homes" },
                ].map((item) => (
                  <Card key={item.title} className="border-2 hover:border-blue-200 transition-colors">
                    <CardContent className="p-6 text-center">
                      <item.icon className="h-10 w-10 text-blue-600 mx-auto mb-3" />
                      <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Why Mitigation Matters */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Mitigation Matters</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">Protecting your family from radon exposure is essential for long-term health</p>
            </div>
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {[
                { stat: "#1", desc: "Cause of lung cancer in non-smokers" },
                { stat: "1 in 6", desc: "Calgary homes have high radon levels" },
                { stat: "3,300", desc: "Radon-related deaths in Canada annually" },
              ].map((item) => (
                <Card key={item.stat} className="border-2 border-red-200 bg-white">
                  <CardContent className="p-8 text-center">
                    <p className="text-5xl font-bold text-red-600 mb-3">{item.stat}</p>
                    <p className="text-gray-700 font-medium">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Health Risks of Radon Exposure</h3>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>When radon is inhaled, radioactive particles can become trapped in your lungs. Over time, these particles release energy that can damage lung tissue and lead to lung cancer.</p>
                <p>The risk is directly related to:</p>
                <ul className="space-y-2 ml-6">
                  <li className="flex items-start space-x-2"><span className="text-red-600 font-bold">•</span><span><span className="font-semibold">Radon concentration</span> - Higher levels mean greater risk</span></li>
                  <li className="flex items-start space-x-2"><span className="text-red-600 font-bold">•</span><span><span className="font-semibold">Duration of exposure</span> - The longer you're exposed, the higher the risk</span></li>
                  <li className="flex items-start space-x-2"><span className="text-red-600 font-bold">•</span><span><span className="font-semibold">Smoking status</span> - Smokers face exponentially higher risks</span></li>
                </ul>
                <div className="bg-blue-50 p-6 rounded-lg mt-6">
                  <p className="font-semibold text-blue-900 mb-2">Good News:</p>
                  <p className="text-gray-700">Radon problems can be fixed! Professional mitigation can reduce radon levels by up to 99%, making your home safe for you and your family.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 px-4 sm:px-6 lg:px-8" data-testid="faq-section">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
              <p className="text-xl text-gray-600">Get answers to common questions about radon</p>
            </div>
            <Accordion type="single" collapsible className="space-y-4">
              {FAQ_ITEMS.map((faq, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="bg-white border-2 rounded-lg px-6 hover:border-blue-200 transition-colors">
                  <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-blue-600">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-700 leading-relaxed">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-blue-600">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Knowledge is Power. Action is Protection.</h2>
            <p className="text-xl text-blue-100 mb-8">Now that you understand the risks, take the first step to protect your family. Book your free radon test today.</p>
            <Link to="/book-test">
              <Button className="bg-white text-blue-600 hover:bg-gray-100 font-semibold px-8 py-6 text-lg shadow-lg" data-testid="radon-cta-btn">
                Book Free Radon Test <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </section>
      </div>
    </main>
  );
}
